import { PANEL_SERVICES, type PanelService } from './servicesPanelServices';

/** The login of a store that Music24 uploads to (the artist-portal card of SoundCloud). */
export type StoreLogin = 'none' | 'connected' | 'expired';

/**
 * What a row can do (design d3). Each one is a button or a link of the row. The uploads of a
 * service (the SoundCloud song, the YouTube videos, the Spotify Canvas) are not row actions: they
 * live in the extras box of the row (design service-extras a1).
 */
export type ServiceRowAction = 'open' | 'edit' | 'paste' | 'findIt';

/** The second line of a row. `link`: the address. `notLinked`: "Not linked". */
export type ServiceRowLine = 'link' | 'notLinked';

export interface ServiceRow extends PanelService {
  /** The link of the song on this service, or null. */
  url: string | null;
  line: ServiceRowLine;
  actions: ServiceRowAction[];
}

export interface ServiceRowsInput {
  serviceLinks: Record<string, string> | null;
  /** The demo artist: Open only (design d3). */
  readOnly: boolean;
}

function plainRow(service: PanelService, url: string | null, readOnly: boolean): ServiceRow {
  if (url !== null) {
    return { ...service, url, line: 'link', actions: readOnly ? ['open'] : ['open', 'edit'] };
  }
  return { ...service, url: null, line: 'notLinked', actions: readOnly ? [] : ['paste', 'findIt'] };
}

/** Every row of the panel, in the fixed order of the services. */
export function buildServiceRows(input: ServiceRowsInput): ServiceRow[] {
  const links = input.serviceLinks ?? {};
  return PANEL_SERVICES.map((service) => {
    const link = links[service.integrationType];
    const url = link === undefined || link.trim() === '' ? null : link;
    return plainRow(service, url, input.readOnly);
  });
}

const NO_HOSTS: ReadonlySet<string> = new Set();

/**
 * True when the row must show also when it is folded: it has a link, or it holds an extras box
 * (`hosts`: the rows of the services with extras, design service-extras a1).
 */
export function rowStaysOpen(row: ServiceRow, hosts: ReadonlySet<string> = NO_HOSTS): boolean {
  return row.url !== null || hosts.has(row.integrationType);
}

/** How many rows show before "Show N more services": 8, or 5 on a phone (design d1, d7). */
export const PANEL_ROWS_SHOWN = 8;
export const PANEL_ROWS_SHOWN_NARROW = 5;

export interface FoldedRows {
  shown: ServiceRow[];
  /** The rows under "Show N more services". */
  folded: ServiceRow[];
}

/** The first `limit` rows, plus every later row with a link or an extras box; the rest fold. */
export function foldServiceRows(
  rows: ServiceRow[],
  limit: number,
  expanded: boolean,
  hosts: ReadonlySet<string> = NO_HOSTS
): FoldedRows {
  if (expanded) {
    return { shown: rows, folded: [] };
  }
  const shown: ServiceRow[] = [];
  const folded: ServiceRow[] = [];
  rows.forEach((row, index) => {
    if (index < limit || rowStaysOpen(row, hosts)) {
      shown.push(row);
    } else {
      folded.push(row);
    }
  });
  return { shown, folded };
}

/** "5 OF 16 LINKED": the rows with an address. The extras boxes never count. */
export function linkedRowCount(rows: ServiceRow[]): number {
  return rows.filter((row) => row.url !== null).length;
}
